import React, { useState, useCallback, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DesignPanelProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onClose: () => void;
}

interface ThemeColors {
  "--primary": string;
  "--primary-foreground": string;
  "--background": string;
  "--foreground": string;
}

interface FontPairing {
  label: string;
  headline: string;
  body: string;
  googleUrl: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FONT_PAIRINGS: FontPairing[] = [
  {
    label: "Inter",
    headline: "Inter",
    body: "Inter",
    googleUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
  },
  {
    label: "Plus Jakarta Sans",
    headline: "Plus Jakarta Sans",
    body: "Plus Jakarta Sans",
    googleUrl: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
  },
  {
    label: "Geist + Inter",
    headline: "Geist",
    body: "Inter",
    googleUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  },
  {
    label: "DM Sans",
    headline: "DM Sans",
    body: "DM Sans",
    googleUrl: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap",
  },
  {
    label: "Bricolage Grotesque",
    headline: "Bricolage Grotesque",
    body: "Inter",
    googleUrl: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap",
  },
  {
    label: "Sora + Inter",
    headline: "Sora",
    body: "Inter",
    googleUrl: "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap",
  },
];

const DEFAULT_COLORS: ThemeColors = {
  "--primary": "#3b82f6",
  "--primary-foreground": "#ffffff",
  "--background": "#0f172a",
  "--foreground": "#f8fafc",
};

const COLOR_FIELDS: Array<{ key: keyof ThemeColors; label: string }> = [
  { key: "--primary", label: "Primary" },
  { key: "--primary-foreground", label: "Primary Text" },
  { key: "--background", label: "Background" },
  { key: "--foreground", label: "Text" },
];

// Convert hex color (#rrggbb) to RGB triplet string (r g b) for CSS variable format
function hexToRgbTriplet(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "59 130 246"; // fallback blue
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `${r} ${g} ${b}`;
}

// Convert RGB triplet string (r g b) to hex (#rrggbb)
function rgbTripletToHex(triplet: string): string {
  const parts = triplet.trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return "#3b82f6";
  const [r, g, b] = parts;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

const RADIUS_STOPS = [
  { label: "Sharp", value: 0 },
  { label: "Subtle", value: 4 },
  { label: "Modern", value: 8 },
  { label: "Rounded", value: 12 },
  { label: "Pill", value: 16 },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Traverse same-origin iframes to find the innermost document (the rendered app).
 * Chain: BuilderWorkspaceState iframe (__preview) → inner iframe (__render) → app document.
 */
function getInnerDocument(
  outerIframe: HTMLIFrameElement | null
): Document | null {
  try {
    const outerDoc = outerIframe?.contentDocument;
    if (!outerDoc) return null;
    // __preview embeds __render as its only iframe
    const inner = outerDoc.querySelector("iframe") as HTMLIFrameElement | null;
    if (inner?.contentDocument) return inner.contentDocument;
    // Fallback: outer doc IS the app (no nesting)
    return outerDoc;
  } catch {
    // Cross-origin — can't access
    return null;
  }
}

function setIframeCssVar(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  property: string,
  value: string
) {
  const doc = getInnerDocument(iframeRef.current);
  if (!doc) return;
  doc.documentElement.style.setProperty(property, value);
}

function injectFontLink(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  url: string
) {
  const doc = getInnerDocument(iframeRef.current);
  if (!doc) return;
  // Don't add duplicate links
  if (doc.head.querySelector(`link[href="${url}"]`)) return;
  const link = doc.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  doc.head.appendChild(link);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DesignPanel({ iframeRef, onClose }: DesignPanelProps) {
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_COLORS);
  const [fontIndex, setFontIndex] = useState<number>(0);
  const [radius, setRadius] = useState<number>(8);
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Read initial values from the iframe on mount
  useEffect(() => {
    const doc = getInnerDocument(iframeRef.current);
    if (!doc) return;
    const style = getComputedStyle(doc.documentElement);
    const read = (prop: string) => style.getPropertyValue(prop).trim();
    const initial: ThemeColors = { ...DEFAULT_COLORS };
    for (const { key } of COLOR_FIELDS) {
      const v = read(key);
      // Convert RGB triplet (e.g. "59 130 246") to hex for color picker
      if (v && /^\d+\s+\d+\s+\d+$/.test(v)) {
        initial[key] = rgbTripletToHex(v);
      } else if (v && v.startsWith("#")) {
        initial[key] = v;
      }
    }
    setColors(initial);
  }, [iframeRef]);

  const applyColor = useCallback(
    (key: keyof ThemeColors, value: string) => {
      setColors((prev) => ({ ...prev, [key]: value }));
      // Debounce rapid color picker changes (native input fires continuously)
      clearTimeout(debounceRefs.current[key]);
      debounceRefs.current[key] = setTimeout(() => {
        // Convert hex to RGB triplet format for CSS variables
        const rgbValue = hexToRgbTriplet(value);
        setIframeCssVar(iframeRef, key, rgbValue);
      }, 16);
    },
    [iframeRef]
  );

  const applyFont = useCallback(
    (index: number) => {
      setFontIndex(index);
      const pairing = FONT_PAIRINGS[index];
      if (!pairing) return;
      injectFontLink(iframeRef, pairing.googleUrl);
      setIframeCssVar(
        iframeRef,
        "--font-headline",
        `'${pairing.headline}', sans-serif`
      );
      setIframeCssVar(
        iframeRef,
        "--font-body",
        `'${pairing.body}', sans-serif`
      );
      // Also set font-family on root div in case the app uses inline style
      const doc = getInnerDocument(iframeRef.current);
      if (doc) {
        const rootDiv = doc.querySelector("#root > div") as HTMLElement | null;
        if (rootDiv) {
          rootDiv.style.fontFamily = `'${pairing.body}', sans-serif`;
        }
      }
    },
    [iframeRef]
  );

  const applyRadius = useCallback(
    (value: number) => {
      setRadius(value);
      const doc = getInnerDocument(iframeRef.current);
      if (!doc) return;
      // Set a CSS variable that components can reference
      doc.documentElement.style.setProperty("--radius", `${value}px`);
      // Also apply to all cards, buttons, and inputs for immediate visual feedback
      const selectors = [
        '[class*="rounded"]',
        "button",
        "input",
        "textarea",
        "select",
        '[class*="card"]',
        '[class*="Card"]',
      ];
      const els = doc.querySelectorAll(selectors.join(","));
      els.forEach((el) => {
        (el as HTMLElement).style.borderRadius = `${value}px`;
      });
    },
    [iframeRef]
  );

  const handleReset = useCallback(() => {
    setColors(DEFAULT_COLORS);
    setFontIndex(0);
    setRadius(8);
    for (const { key } of COLOR_FIELDS) {
      // Convert hex to RGB triplet format
      const rgbValue = hexToRgbTriplet(DEFAULT_COLORS[key]);
      setIframeCssVar(iframeRef, key, rgbValue);
    }
    // Reload iframe to reset all styles
    try {
      (iframeRef as React.RefObject<HTMLIFrameElement | null>)?.current
        ?.contentWindow?.location?.reload();
    } catch {
      // ignore
    }
  }, [iframeRef]);

  const radiusLabel =
    RADIUS_STOPS.find((s) => s.value === radius)?.label ??
    `${radius}px`;

  return (
    <div className="absolute top-0 right-0 bottom-0 w-[260px] bg-card border-l border-border z-30 flex flex-col shadow-xl">
      {/* Header */}
      <div className="h-10 flex items-center justify-between px-3 border-b border-border flex-shrink-0">
        <span className="text-xs font-semibold text-foreground tracking-wide uppercase">
          Design
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleReset}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            title="Reset to defaults"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            title="Close design panel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-5">
        {/* ── Theme Colors ── */}
        <section>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
            Theme Colors
          </h4>
          <div className="space-y-2">
            {COLOR_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between gap-2">
                <label className="text-xs text-foreground/80 font-medium min-w-[70px]">
                  {label}
                </label>
                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <input
                      type="color"
                      value={colors[key]}
                      onChange={(e) => applyColor(key, e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div
                      className="w-7 h-7 rounded-md border border-border shadow-sm cursor-pointer"
                      style={{ backgroundColor: colors[key] }}
                    />
                  </div>
                  <input
                    type="text"
                    value={colors[key]}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                        setColors((prev) => ({ ...prev, [key]: v }));
                        if (v.length === 7) applyColor(key, v);
                      }
                    }}
                    className="w-[72px] h-7 px-1.5 text-[11px] font-mono bg-muted/50 border border-border rounded text-foreground/80 focus:outline-none focus:ring-1 focus:ring-primary/30"
                    spellCheck={false}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Font Selector ── */}
        <section>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
            Font
          </h4>
          <div className="space-y-1">
            {FONT_PAIRINGS.map((pairing, i) => (
              <button
                key={pairing.label}
                onClick={() => applyFont(i)}
                className={cn(
                  "w-full text-left px-2.5 py-2 rounded-md text-xs transition-colors",
                  i === fontIndex
                    ? "bg-primary/15 text-primary border border-primary/25"
                    : "text-foreground/70 hover:bg-muted/50 border border-transparent"
                )}
              >
                <span className="font-medium">{pairing.label}</span>
                {pairing.headline !== pairing.body && (
                  <span className="text-[10px] text-muted-foreground ml-1.5">
                    + {pairing.body}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* ── Border Radius ── */}
        <section>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
            Border Radius
            <span className="ml-2 text-[10px] font-normal text-muted-foreground/70 normal-case">
              {radiusLabel}
            </span>
          </h4>
          <div className="px-0.5">
            <input
              type="range"
              min={0}
              max={16}
              step={1}
              value={radius}
              onChange={(e) => applyRadius(Number(e.target.value))}
              className="w-full h-1.5 accent-primary cursor-pointer"
            />
            <div className="flex justify-between mt-1">
              {RADIUS_STOPS.map((stop) => (
                <button
                  key={stop.value}
                  onClick={() => applyRadius(stop.value)}
                  className={cn(
                    "text-[9px] transition-colors",
                    radius === stop.value
                      ? "text-primary font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {stop.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── Preview Shapes ── */}
        <section>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
            Preview
          </h4>
          <div className="space-y-2">
            {/* Sample card */}
            <div
              className="p-3 border border-border"
              style={{
                borderRadius: `${radius}px`,
                backgroundColor: colors["--background"],
                color: colors["--foreground"],
              }}
            >
              <div
                className="h-1.5 w-16 mb-2"
                style={{
                  borderRadius: `${radius}px`,
                  backgroundColor: colors["--primary"],
                }}
              />
              <div className="text-[10px] font-medium" style={{ fontFamily: FONT_PAIRINGS[fontIndex]?.headline }}>
                Sample Card
              </div>
              <div className="text-[9px] mt-0.5 opacity-60">
                Preview of your theme
              </div>
              <button
                className="mt-2 px-2.5 py-1 text-[9px] font-medium"
                style={{
                  borderRadius: `${Math.min(radius, 8)}px`,
                  backgroundColor: colors["--primary"],
                  color: colors["--primary-foreground"],
                }}
              >
                Button
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
